import React, {
  useState,
  useCallback,
  useContext,
  createContext,
  useMemo,
} from 'react';
import api from 'services';
import { useNewToast } from 'hooks/newToast';
import { appWarnings } from 'utils/appWarnings';
import { formatCooperativa } from 'utils/formaters/rastreabilidade/cooperativa';

const successful = {
  type: 'success',
  message: 'Depósito na cooperativa gerado com sucesso!',
};

const CooperativaDepositoContext = createContext({});

export const CooperativaDepositoProvider = ({ children }) => {
  const { showToast } = useNewToast();

  const [deposits, setDeposits] = useState([]);
  const [deposit, setDeposit] = useState({});
  const [depositsLoading, setDepositsLoading] = useState(false);
  const [depositLoading, setDepositLoading] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const getAllDeposits = useCallback(
    async (filter) => {
      try {
        setDepositsLoading(true);
        const response = await api.cooperativaDeposito().getAllDeposits(filter);
        setDeposits(response);
      } catch (err) {
        showToast(appWarnings.Danger(err));
      } finally {
        setDepositsLoading(false);
      }
    },
    [showToast],
  );

  const getDepositById = useCallback(
    async (id) => {
      try {
        setDepositLoading(true);
        const response = await api.cooperativaDeposito().getDepositById(id);
        setDeposit(response);
      } catch (err) {
        showToast(appWarnings.Danger(err));
      } finally {
        setDepositLoading(false);
      }
    },
    [showToast],
  );

  const resetDeposit = useCallback(() => {
    setDeposit({});
  }, []);

  const createDeposit = useCallback(
    async (data) => {
      const tempData = formatCooperativa(data);
      try {
        setCreateLoading(true);
        const response = await api
          .cooperativaDeposito()
          .createDeposit(tempData);
        showToast(successful);
        return response;
      } catch (err) {
        showToast(appWarnings.Danger(err));
        throw new Error(err);
      } finally {
        setCreateLoading(false);
      }
    },
    [showToast],
  );

  const updateDeposit = useCallback(
    async (id, data) => {
      try {
        const tempData = formatCooperativa(data);
        setUpdateLoading(true);
        const response = await api
          .cooperativaDeposito()
          .updateDeposit(id, tempData);
        showToast({
          ...successful,
          message: 'Depósito Cooperativo atualizado com sucesso!',
        });
        return response;
      } catch (err) {
        showToast(appWarnings.Danger(err));
        throw new Error(err);
      } finally {
        setUpdateLoading(false);
      }
    },
    [showToast],
  );

  const deleteDeposit = useCallback(
    async (id) => {
      try {
        setDeleteLoading(true);
        await api.cooperativaDeposito().deleteDeposit(id);
        setDeposits((prev) => prev.filter((item) => item.id !== id));
        showToast({
          ...successful,
          message: 'Depósito Cooperativo removido com sucesso!',
        });
      } catch (err) {
        showToast(appWarnings.Danger(err));
        throw new Error(err);
      } finally {
        setDeleteLoading(false);
      }
    },
    [showToast],
  );

  const value = useMemo(
    () => ({
      deposits,
      deposit,
      depositsLoading,
      depositLoading,
      createLoading,
      updateLoading,
      deleteLoading,
      getAllDeposits,
      getDepositById,
      createDeposit,
      updateDeposit,
      deleteDeposit,
      resetDeposit,
    }),
    [
      deposits,
      deposit,
      depositsLoading,
      depositLoading,
      createLoading,
      updateLoading,
      deleteLoading,
      getAllDeposits,
      getDepositById,
      createDeposit,
      updateDeposit,
      deleteDeposit,
      resetDeposit,
    ],
  );

  return (
    <CooperativaDepositoContext.Provider value={value}>
      {children}
    </CooperativaDepositoContext.Provider>
  );
};

export function useCooperativaDeposito() {
  const context = useContext(CooperativaDepositoContext);

  if (!context)
    throw new Error('useCooperativaDeposito must be used within a Provider');

  return context;
}
